import { useState, useRef, useEffect, useMemo } from "react";

/**
 * Lightweight searchable dropdown (no external deps).
 * options: [{ value, label }]
 */
const SearchableSelect = ({
    options = [],
    value = "",
    onChange,
    placeholder = "-- Select --",
    className = "",
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState("");
    const wrapperRef = useRef(null);

    const selectedOption = useMemo(
        () => options.find((opt) => String(opt.value) === String(value)),
        [options, value]
    );

    const filteredOptions = useMemo(() => {
        if (!query) return options;
        const q = query.toLowerCase();
        return options.filter((opt) => opt.label.toLowerCase().includes(q));
    }, [options, query]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setIsOpen(false);
                setQuery("");
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelect = (opt) => {
        onChange(opt ? opt.value : "");
        setQuery("");
        setIsOpen(false);
    };

    return (
        <div
            ref={wrapperRef}
            className={`relative w-full min-w-0 ${className}`}
            style={isOpen ? { position: "relative", zIndex: 9999 } : undefined}
        >
            <input
                type="text"
                className="border rounded px-3 py-2 border-lGrey w-full min-w-0"
                placeholder={placeholder}
                value={isOpen ? query : selectedOption?.label || ""}
                onFocus={() => {
                    setIsOpen(true);
                    setQuery("");
                }}
                onChange={(e) => setQuery(e.target.value)}
            />
            {isOpen && (
                <ul
                    className="absolute mt-1 w-full max-h-60 overflow-y-auto border border-lGrey rounded shadow-lg"
                    style={{ backgroundColor: "#ffffff", zIndex: 9999 }}
                >
                    <li
                        className="px-3 py-2 text-gray-500 hover:bg-gray-100 cursor-pointer"
                        onMouseDown={() => handleSelect(null)}
                    >
                        {placeholder}
                    </li>
                    {filteredOptions.length === 0 ? (
                        <li className="px-3 py-2 text-gray-400">No matches found</li>
                    ) : (
                        filteredOptions.map((opt) => (
                            <li
                                key={opt.value}
                                className={`px-3 py-2 hover:bg-gray-100 cursor-pointer bg-white ${String(opt.value) === String(value) ? "bg-gray-50 font-medium" : ""
                                    }`}
                                onMouseDown={() => handleSelect(opt)}
                            >
                                {opt.label}
                            </li>
                        ))
                    )}
                </ul>
            )}
        </div>
    );
};

export default SearchableSelect;
